import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Pencil, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { fmtDate } from "@/lib/format";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { confirmAction } from "@/lib/dialogs";
export const SUGGESTIONS = [
  "Portfolio status report: which projects are at risk?",
  "Stock valuation and slow-moving materials",
  "Vendor-wise procurement summary with pending deliveries",
  "Client billing and receivables by project",
];

const SPINNER = ["·", "✢", "✳", "✶", "✻", "✽", "✻", "✶", "✳", "✢"];
const VERBS = [
  "Combobulating", "Cross-checking", "Tallying", "Surveying", "Reconciling", "Crunching",
  "Measuring twice", "Pondering", "Bricklaying", "Pouring concrete", "Levelling", "Shuttering",
];

// Claude-style "thinking" line: twinkling glyph, a rotating verb and elapsed seconds.
export function Thinking() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 120);
    return () => clearInterval(id);
  }, []);
  const verb = VERBS[Math.floor(tick / 20) % VERBS.length]; // new verb every ~2.4s
  return (
    <div className="flex items-center gap-2 text-sm" role="status" aria-live="polite" data-testid="ai-assistant-thinking">
      <span className="w-4 text-center text-base text-primary" aria-hidden="true">{SPINNER[tick % SPINNER.length]}</span>
      <span className="font-medium text-primary">{verb}…</span>
      <span className="text-xs text-muted-foreground">({Math.floor((tick * 120) / 1000)}s)</span>
    </div>
  );
}

function inline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part
  );
}

const cellsOf = (row) => row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

// Renders the subset of markdown the assistant is told to use: headings, bold, bullets, tables.
/** "[[guide:module/workflow]]" or "[[journey:stage]]" on its own line: a button that starts that guided tour. */
const GUIDE_LINE = /^\s*\[\[(guide|journey):([\w-]+)(?:\/([\w-]+))?\]\]\s*$/;
function startGuide(kind, a, b) {
  const detail = kind === "journey" ? { journey: true, stageId: a } : { moduleId: a, workflowId: b };
  window.dispatchEvent(new CustomEvent("buildnexa:start-guide", { detail }));
}

export function Markdown({ text }) {
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim().startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(lines[i++]);
      const [head, ...body] = rows.filter((r) => !/^[\s|:-]+$/.test(r)).map(cellsOf);
      out.push(
        <div key={out.length} className="overflow-x-auto rounded-lg border">
          <table className="w-full text-xs">
            <thead className="bg-muted/60">
              <tr>{head.map((c, j) => <th key={j} className="px-2 py-1.5 text-left font-semibold">{inline(c)}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((r, k) => (
                <tr key={k} className="border-t">{r.map((c, j) => <td key={j} className="whitespace-nowrap px-2 py-1.5">{inline(c)}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^\s*[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*] /.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*] /, ""));
      out.push(<ul key={out.length} className="list-disc space-y-1 pl-5">{items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ul>);
      continue;
    }
    const guide = line.match(GUIDE_LINE);
    if (guide) {
      out.push(<button key={out.length} type="button" onClick={() => startGuide(guide[1], guide[2], guide[3])} data-testid="start-guide-from-chat"
        className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">▶ Show me on screen</button>);
      i++;
      continue;
    }
    if (/^\s*\d+[.)] /.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+[.)] /.test(lines[i])) items.push(lines[i++].replace(/^\s*\d+[.)] /, ""));
      out.push(<ol key={out.length} className="list-decimal space-y-1 pl-5">{items.map((t, j) => <li key={j}>{inline(t)}</li>)}</ol>);
      continue;
    }
    if (/^#{1,6} /.test(line)) out.push(<p key={out.length} className="font-semibold">{inline(line.replace(/^#+ /, ""))}</p>);
    else if (line.trim()) out.push(<p key={out.length}>{inline(line)}</p>);
    i++;
  }
  return <div className="space-y-2">{out}</div>;
}

function ChatList({ chats, activeId, onOpen, onNew, onRename, onDelete, busy }) {
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");

  const saveTitle = (chat) => {
    const t = title.trim();
    setEditing(null);
    if (t && t !== chat.title) onRename(chat, t);
  };

  return (
    <aside className="flex max-h-48 flex-col rounded-xl border bg-card lg:max-h-none" data-testid="ask-buildnexa-history">
      <div className="border-b p-3">
        <Button variant="outline" size="sm" className="w-full gap-2" onClick={onNew} disabled={busy} data-testid="ask-buildnexa-new-chat">
          <Plus className="h-4 w-4" /> New chat
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {chats.length === 0 && <p className="px-2 py-3 text-xs text-muted-foreground">Your chats will appear here.</p>}
        {chats.map((c) => (
          <div
            key={c.id}
            className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm ${c.id === activeId ? "bg-muted font-medium" : "hover:bg-muted/60"}`}
          >
            {editing === c.id ? (
              <input
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => saveTitle(c)}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(c); if (e.key === "Escape") setEditing(null); }}
                maxLength={200}
                aria-label="Chat title"
                className="min-w-0 flex-1 rounded border bg-background px-1.5 py-0.5 text-sm"
                data-testid="ask-buildnexa-rename-input"
              />
            ) : (
              <button
                onClick={() => onOpen(c.id)}
                disabled={busy}
                className="min-w-0 flex-1 text-left"
                title={c.title}
                data-testid="ask-buildnexa-chat"
              >
                <span className="block truncate">{c.title}</span>
                <span className="block text-[11px] font-normal text-muted-foreground">{fmtDate(c.updated_at)}</span>
              </button>
            )}
            {editing !== c.id && (
              <span className="flex shrink-0 lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100">
                <button
                  onClick={() => { setEditing(c.id); setTitle(c.title); }}
                  className="rounded p-1 text-muted-foreground hover:text-foreground"
                  aria-label={`Rename ${c.title}`}
                  data-testid="ask-buildnexa-rename"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(c)}
                  className="rounded p-1 text-muted-foreground hover:text-destructive"
                  aria-label={`Delete ${c.title}`}
                  data-testid="ask-buildnexa-delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
}

export default function AskBuildNexa() {
  const { selectedCompanyId } = useCompany() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get("chat");
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const justCreated = useRef(null); // a chat this page just started: its messages are already on screen

  const loadChats = useCallback(async () => {
    try {
      setChats((await api.get("/ai/chats")).data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);

  // open the chat named in the URL (?chat=<id>), so a refresh lands back on it
  useEffect(() => {
    if (!chatId) { setMessages([]); return undefined; }
    if (justCreated.current === chatId) return undefined;
    let alive = true;
    api.get(`/ai/chats/${chatId}`)
      .then(({ data }) => alive && setMessages(data.messages))
      .catch((err) => {
        if (!alive) return;
        toast.error(getErrorMessage(err));
        setSearchParams({});
      });
    return () => { alive = false; };
  }, [chatId, setSearchParams]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const openChat = (id) => { setInput(""); setSearchParams(id ? { chat: id } : {}); };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    const before = messages;
    setMessages([...before, { role: "user", content }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { message: content, chat_id: chatId || null, company_id: selectedCompanyId || null });
      setMessages([...before, { role: "user", content }, { role: "assistant", content: data.reply }]);
      if (!chatId) {
        justCreated.current = data.chat_id;
        setSearchParams({ chat: data.chat_id });
      }
      loadChats();
    } catch (err) {
      toast.error(getErrorMessage(err));
      setMessages(before);
      setInput(content);
    } finally {
      setBusy(false);
    }
  };

  const renameChat = async (chat, title) => {
    try {
      await api.patch(`/ai/chats/${chat.id}`, { title });
      loadChats();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const deleteChat = async (chat) => {
    if (!await confirmAction(`Delete the chat "${chat.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/ai/chats/${chat.id}`);
      if (chat.id === chatId) openChat(null);
      loadChats();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="ask-buildnexa-page">
      <PageHeader
        title="Ask BuildNexa"
        description="Reports from your live project, stock, procurement and billing data."
        testId="ask-buildnexa-header"
      />
      <div className="grid gap-4 lg:h-[calc(100vh-15rem)] lg:min-h-[420px] lg:grid-cols-[16rem_1fr]">
        <ChatList
          chats={chats}
          activeId={chatId}
          onOpen={openChat}
          onNew={() => openChat(null)}
          onRename={renameChat}
          onDelete={deleteChat}
          busy={busy}
        />
        <div className="flex h-[calc(100vh-15rem)] min-h-[420px] flex-col rounded-xl border bg-card lg:h-auto lg:min-h-0">
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
            {messages.length === 0 && (
              <div className="mx-auto max-w-2xl space-y-3 py-6 text-center">
                <Sparkles className="mx-auto h-8 w-8 text-primary" aria-hidden="true" />
                <p className="text-muted-foreground">Ask for a report, or try one of these:</p>
                <div className="grid gap-2 text-left sm:grid-cols-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      className="rounded-lg border px-3 py-2 hover:bg-muted"
                      data-testid="ask-buildnexa-suggestion"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) =>
              m.role === "user" ? (
                <div key={i} className="ml-auto max-w-[85%] rounded-xl bg-primary px-3 py-2 text-primary-foreground">{m.content}</div>
              ) : (
                <div key={i} className="max-w-full rounded-xl bg-muted/50 px-3 py-2"><Markdown text={m.content} /></div>
              )
            )}
            {busy && <Thinking />}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-end gap-2 border-t px-5 py-3"
          >
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask for a report, e.g. stock of TMT bars"
              rows={2}
              className="min-h-0 resize-none"
              aria-label="Message"
              data-testid="ask-buildnexa-input"
            />
            <Button type="submit" size="icon" disabled={busy || !input.trim()} aria-label="Send" data-testid="ask-buildnexa-send">
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
